import { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { WorkspaceView } from "@/components/WorkspaceView";
import WorkspacePublishButton from "@/components/WorkspacePublishButton";
import { Code02Icon } from "@/icons";
import ErrorPage from "@/pages/_error";
import { Environment, graphql } from "react-relay";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { WorkspacePageQuery as WorkspacePageQueryType } from "./__generated__/WorkspacePageQuery.graphql";
import { parse } from "valibot";
import { WorkspacePageParams } from "@/utils/workspace";
export default function WorkspacePage(): ReactNode {
  const intl = useIntl();
  const {
    query: { workspaceBySlug: workspace },
  } = useWorkspacePageQuery();
  if (!workspace) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({ defaultMessage: "Workspace not found" })}
      />
    );
  }
  const current = workspace.versions.nodes[0];
  if (!current) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 bg-brand-25 rounded-xl p-8 mt-4 lg:h-60 h-auto">
        <Code02Icon className="h-8 w-8 text-green-600" />
        <div className="flex flex-col items-center justify-center gap-1">
          <p className="text-dxs font-semibold text-grey-950">
            <FormattedMessage defaultMessage="Looks like there is nothing to see yet" />
          </p>
          <p className="text-md font-normal text-grey-700">
            {workspace.viewerCanEdit ? (
              <FormattedMessage defaultMessage="This workspace doesn't have any versions yet. Create one to get started!" />
            ) : (
              <FormattedMessage defaultMessage="Stay tuned for updates from the author." />
            )}
          </p>
        </div>
        <WorkspacePublishButton workspace={workspace}>
          <FormattedMessage defaultMessage="Create version" />
        </WorkspacePublishButton>
      </div>
    );
  }
  return <WorkspaceView workspace={current} className="w-full h-full" />;
}
const WorkspacePageQuery = graphql`
  query WorkspacePageQuery($owner: String!, $slug: String!) {
    workspaceBySlug(owner: $owner, slug: $slug) {
      name
      versions(first: 1) {
        nodes {
          ...WorkspaceViewFragment
        }
      }
      owner {
        username
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
      viewerCanEdit: viewerCan(action: UPDATE_WORKSPACE)
      ...WorkspacePublishButtonFragment
    }
  }
`;
export const loader =
  (environment: Environment) =>
  ({ params }: LoaderArgs) =>
    preload<WorkspacePageQueryType>(
      environment,
      WorkspacePageQuery,
      parse(WorkspacePageParams, params),
      { fetchPolicy: "store-and-network" },
    );
const useWorkspacePageQuery = () => usePreloaded<WorkspacePageQueryType>();
